import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, BriefcaseIcon, CheckIcon, ClockIcon, LocationIcon, ShieldCheckIcon } from "@/components/icons";
import { JobCard } from "@/components/job-card";
import { formatSalary, initials, jobTitle, relativeDate, stringList } from "@/lib/format";
import type { JobDetail, JobSummary } from "@/lib/types";

function DetailSection({ title, items }: { title: string; items?: string | string[] | null }) {
  const values = stringList(items);
  if (!values.length) return null;
  return (
    <section className="job-detail-section">
      <h2>{title}</h2>
      {values.length === 1 ? <p>{values[0]}</p> : <ul>{values.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>}
    </section>
  );
}

export function JobDetailContent({
  job,
  related,
  authLoading,
  onApply,
}: {
  job: JobDetail;
  related: JobSummary[];
  authLoading: boolean;
  onApply: () => void;
}) {
  const title = jobTitle(job);
  const salary = formatSalary(job);
  const searchTerm = title.split(/\s+/).slice(0, 3).join(" ");

  return (
    <main id="main" className="job-page">
      <div className="shell">
        <Link className="back-link" to="/jobs"><ArrowLeft /> Back to jobs</Link>
        <div className="job-page__layout">
          <article className="job-detail">
            <header className="job-detail__header">
              <div className="job-detail__company">
                <div className="company-avatar company-avatar--violet company-avatar--large">{initials(job.company)}</div>
                <div><span>Hiring company</span><strong>{job.company || "Employer"}</strong></div>
              </div>
              <p className="verified-label"><CheckIcon /> {relativeDate(job.last_seen_date)}</p>
              <h1>{title}</h1>
              <div className="job-detail__facts">
                {job.location ? <span><LocationIcon /> {job.location}</span> : null}
                {job.employment_type || job.type ? <span><BriefcaseIcon /> {job.employment_type || job.type}</span> : null}
                {job.work_model ? <span><ClockIcon /> {job.work_model}</span> : null}
              </div>
            </header>
            <div className="job-detail__content">
              <DetailSection title="About the role" items={job.job_description} />
              <DetailSection title="What you’ll do" items={job.responsibilities} />
              <DetailSection title="What you’ll bring" items={job.minimum_qualifications} />
              <DetailSection title="Nice to have" items={job.preferred_qualifications} />
              {job.skills_required?.length ? <section className="job-detail-section"><h2>Skills</h2><div className="skill-list">{job.skills_required.map((skill) => <span key={skill}>{skill}</span>)}</div></section> : null}
              <DetailSection title="Benefits" items={job.benefits} />
              <DetailSection title="Education" items={job.education_level} />
              <DetailSection title="Work authorisation" items={job.work_authorization_requirements} />
            </div>
          </article>

          <aside className="apply-panel">
            <p className="apply-panel__label">Role summary</p>
            <h2 className="apply-panel__title">{title}</h2>
            <dl>
              {job.company ? <div><dt>Company</dt><dd>{job.company}</dd></div> : null}
              {job.location ? <div><dt>Location</dt><dd>{job.location}</dd></div> : null}
              {job.work_model ? <div><dt>Work model</dt><dd>{job.work_model}</dd></div> : null}
              {job.experience_level ? <div><dt>Level</dt><dd>{job.experience_level}</dd></div> : null}
              {job.years_experience_numeric ? <div><dt>Experience</dt><dd>{job.years_experience_numeric} years</dd></div> : null}
              {salary ? <div><dt>Compensation</dt><dd>{salary}</dd></div> : null}
              {job.schedule ? <div><dt>Schedule</dt><dd>{job.schedule}</dd></div> : null}
            </dl>
            <button className="button button--dark apply-button" type="button" disabled={authLoading} onClick={onApply}>
              Continue to apply <ArrowUpRight />
            </button>
            <p className="apply-panel__note"><ShieldCheckIcon /> Sign in required.</p>
          </aside>
        </div>
      </div>

      {related.length ? <section className="related-jobs section"><div className="shell"><div className="section-heading"><div><p className="section-kicker">Keep exploring</p><h2>Similar opportunities.</h2></div><Link className="text-link" to={`/jobs?q=${encodeURIComponent(searchTerm)}`}>See all similar roles <ArrowUpRight /></Link></div><div className="results-grid">{related.map((item) => <JobCard job={item} key={item.job_id} />)}</div></div></section> : null}
    </main>
  );
}
